import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import {
  Line,
  Bar,
  Doughnut
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import moment from 'moment';
import './auth_analytics.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import AuthlyticsDashboard from './authlytics_dashboard/authlytics_dashboard';
import AdvancedAuthlytics from './authlytics_dashboard/advanced_authlytics';

// # Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// # Add chart color palette
const chartColors = {
  primary: '#7CB9E8',    // # Vibrant pastel blue
  secondary: '#B784A7',  // # Rich pastel purple
  accent: '#FF9A8B',     // # Vibrant pastel coral
  success: '#77DD77',    // # Vibrant pastel green
  warning: '#FFB347',    // # Vibrant pastel orange
  complementary: [
    '#B784A7',  // # Purple
    '#FF9A8B',  // # Coral
    '#77DD77',  // # Green
    '#FFB347',  // # Orange
    '#7CB9E8'   // # Blue
  ]
};

const AuthAnalytics = () => {
  const navigate = useNavigate();
  
  // # State management
  const [activeTab, setActiveTab] = useState('general');
  const [selectedUser, setSelectedUser] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD')
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalLogins: 0,
    uniqueUsers: 0,
    averageSessionDuration: 0,
    peakHours: Array(24).fill(0), // # Initialize array for 24 hours
    deviceStats: {},
    weekdayStats: {},
    monthlyTrends: [],
    userSpecificData: null,
    sessionLengths: []
  });
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [longTermData, setLongTermData] = useState({
    dailyLogins: {},
    dailyLogouts: {},
    averageSessions: {}
  });
  const [timeRange, setTimeRange] = useState('month');
  const [customRange, setCustomRange] = useState({
    start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    end: moment().format('YYYY-MM-DD')
  });

  // # Function to calculate session length in minutes
  const calculateSessionLength = (signIn, signOut) => {
    const start = moment(signIn.timestamp);
    const end = moment(signOut.timestamp);
    return end.diff(start, 'minutes', true);
  };

  // # Fetch users list for search
  const fetchUsersList = async () => {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setUsersList(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // # Effect to fetch users list when dialog opens
  useEffect(() => {
    if (searchDialogOpen) {
      fetchUsersList();
    }
  }, [searchDialogOpen]);

  // # Fetch analytics data
  const fetchAnalyticsData = async (userId = null) => {
    try {
      const activitiesRef = collection(db, 'activities');
      let querySnapshot;
      let userProfile = null;

      if (userId) {
        // # Get specific user document directly by ID
        const userDocRef = doc(activitiesRef, userId);
        const userDoc = await getDoc(userDocRef);
        querySnapshot = userDoc.exists() ? [userDoc] : [];

        // # Fetch user profile data
        const userProfileRef = doc(db, 'users', userId);
        const userProfileDoc = await getDoc(userProfileRef);
        if (userProfileDoc.exists()) {
          userProfile = userProfileDoc.data();
        }
        setSelectedUserProfile(userProfile);
      } else {
        // # Get all documents for general analytics
        querySnapshot = await getDocs(activitiesRef);
        setSelectedUserProfile(null);
      }

      const processedData = {
        totalLogins: 0,
        uniqueUsers: new Set(),
        deviceStats: {},
        weekdayStats: {
          'Sunday': 0,
          'Monday': 0,
          'Tuesday': 0,
          'Wednesday': 0,
          'Thursday': 0,
          'Friday': 0,
          'Saturday': 0
        },
        monthlyTrends: [],
        sessionLengths: [],
        peakHours: Array(24).fill(0)
      };

      const startDate = moment(dateRange.start);
      const endDate = moment(dateRange.end).endOf('day');

      // # Process documents
      (Array.isArray(querySnapshot) ? querySnapshot : querySnapshot.docs).forEach((doc) => {
        const data = doc.data();
        const uid = doc.id;
        
        // # Process each month's data
        Object.entries(data).forEach(([key, monthData]) => {
          if (key === 'uid') return;
          
          // # Check if this is a valid month entry (format: YYYY_MM)
          if (!key.match(/^\d{4}_\d{2}$/)) return;

          const authHistory = monthData.auth_history || {};
          const signIns = authHistory.sign_ins || {};
          const signOuts = authHistory.sign_outs || {};

          // # Create a map of sign-outs by their timestamps for faster lookup
          const signOutMap = new Map();
          Object.entries(signOuts).forEach(([signOutId, signOut]) => {
            signOutMap.set(moment(signOut.timestamp).valueOf(), signOut);
          });

          // # Process sign ins
          Object.entries(signIns).forEach(([signInId, signIn]) => {
            const signInTime = moment(signIn.timestamp);

            // # Check if the login is within the selected date range
            if (signInTime.isBetween(startDate, endDate, null, '[]')) {
              processedData.totalLogins++;
              processedData.uniqueUsers.add(uid);

              // # Process device stats
              const device = signIn.device;
              const deviceType = device.split(' - ')[0]; // Extract device type (Win32, Linux, etc.)
              processedData.deviceStats[deviceType] = (processedData.deviceStats[deviceType] || 0) + 1;

              // # Process weekday stats
              const weekday = signInTime.format('dddd');
              processedData.weekdayStats[weekday]++;

              // # Process hour of day stats
              const hour = signInTime.hour();
              processedData.peakHours[hour]++;

              // # Find matching sign out
              // # First try to find the next sign out after this sign in
              const signOutEntries = Array.from(signOutMap.entries())
                .filter(([timestamp]) => timestamp > signInTime.valueOf())
                .sort(([a], [b]) => a - b);

              if (signOutEntries.length > 0) {
                const [, matchingSignOut] = signOutEntries[0];
                const sessionLength = calculateSessionLength(signIn, matchingSignOut);
                if (sessionLength >= 0 && sessionLength < 24 * 60) { // Filter out sessions longer than 24 hours
                  processedData.sessionLengths.push(sessionLength);
                }
              }
            }
          });
        });
      });

      // # Calculate average session duration
      const averageSessionDuration = processedData.sessionLengths.length > 0
        ? processedData.sessionLengths.reduce((a, b) => a + b, 0) / processedData.sessionLengths.length
        : 0;

      setAnalyticsData({
        ...processedData,
        uniqueUsers: processedData.uniqueUsers.size,
        averageSessionDuration: Math.round(averageSessionDuration * 10) / 10,
        weekdayStats: processedData.weekdayStats // Now maintains order
      });

      // # Add long-term data processing
      const dailyLogins = {};
      const dailyLogouts = {};
      const sessionsByDay = {};

      // # Process documents for long-term trends
      (Array.isArray(querySnapshot) ? querySnapshot : querySnapshot.docs).forEach((doc) => {
        const data = doc.data();
        
        Object.entries(data).forEach(([key, monthData]) => {
          if (key === 'uid') return;
          if (!key.match(/^\d{4}_\d{2}$/)) return;

          const authHistory = monthData.auth_history || {};
          const signIns = authHistory.sign_ins || {};
          const signOuts = authHistory.sign_outs || {};

          // # Process sign-ins by day
          Object.values(signIns).forEach(signIn => {
            const date = moment(signIn.timestamp).format('YYYY-MM-DD');
            dailyLogins[date] = (dailyLogins[date] || 0) + 1;
          });

          // # Process sign-outs by day
          Object.values(signOuts).forEach(signOut => {
            const date = moment(signOut.timestamp).format('YYYY-MM-DD');
            dailyLogouts[date] = (dailyLogouts[date] || 0) + 1;
          });

          // # Calculate average session length by day
          Object.values(signIns).forEach(signIn => {
            const signInTime = moment(signIn.timestamp);
            const date = signInTime.format('YYYY-MM-DD');
            
            // # Find matching sign-out
            const matchingSignOut = Object.values(signOuts).find(signOut => 
              moment(signOut.timestamp).isAfter(signInTime)
            );

            if (matchingSignOut) {
              const sessionLength = calculateSessionLength(signIn, matchingSignOut);
              if (sessionLength >= 0 && sessionLength < 24 * 60) {
                if (!sessionsByDay[date]) {
                  sessionsByDay[date] = { total: 0, count: 0 };
                }
                sessionsByDay[date].total += sessionLength;
                sessionsByDay[date].count++;
              }
            }
          });
        });
      });

      // # Calculate average session length by day
      const averageSessions = {};
      Object.entries(sessionsByDay).forEach(([date, data]) => {
        averageSessions[date] = data.total / data.count;
      });

      setLongTermData({
        dailyLogins,
        dailyLogouts,
        averageSessions
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setSelectedUserProfile(null);
    }
  };

  // # Handle user selection from dialog
  const handleUserSelect = (user) => {
    setSelectedUser(user.uid);
    setSearchDialogOpen(false);
    setSearchTerm('');
  };

  // # Filter users based on search term
  const filteredUsers = usersList.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // # Render user search dialog
  const renderUserSearchDialog = () => (
    <Dialog 
      open={searchDialogOpen} 
      onClose={() => setSearchDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Select User
        <IconButton
          aria-label="close"
          onClick={() => setSearchDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          placeholder="Search by name or email..."
          fullWidth
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        <List sx={{ mt: 2 }}>
          {filteredUsers.map((user) => (
            <ListItem 
              button 
              key={user.uid}
              onClick={() => handleUserSelect(user)}
            >
              <ListItemAvatar>
                <Avatar src={user.photoURL || '/icons/pfp.png'} />
              </ListItemAvatar>
              <ListItemText 
                primary={`${user.firstName} ${user.lastName}`}
                secondary={user.email}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );

  // # Effect to fetch data on component mount and when filters change
  useEffect(() => {
    fetchAnalyticsData(selectedUser);
  }, [selectedUser, dateRange]);

  // # Render stats dashboard
  const renderStatsDashboard = () => {
    // # Calculate session length statistics
    const sessionStats = analyticsData.sessionLengths.length > 0 ? {
      min: Math.min(...analyticsData.sessionLengths),
      max: Math.max(...analyticsData.sessionLengths),
      avg: analyticsData.averageSessionDuration
    } : { min: 0, max: 0, avg: 0 };

    return (
      <div className="auth_analytics_stats-grid">
        {selectedUserProfile && (
          <div className="auth_analytics_user-profile-card">
            <div className="auth_analytics_user-profile-header">
              <img 
                src={selectedUserProfile.photoURL || '/icons/pfp.png'} 
                alt="Profile" 
                className="auth_analytics_user-avatar"
              />
              <div className="auth_analytics_user-info">
                <h3>{`${selectedUserProfile.firstName} ${selectedUserProfile.lastName}`}</h3>
                <p className="auth_analytics_user-email">{selectedUserProfile.email}</p>
                <p className="auth_analytics_user-username">@{selectedUserProfile.username}</p>
              </div>
            </div>
          </div>
        )}
        <div className="auth_analytics_stat-card">
          <h3>Total Logins</h3>
          <div className="auth_analytics_stat-value">{analyticsData.totalLogins}</div>
        </div>
        <div className="auth_analytics_stat-card">
          <h3>Unique Users</h3>
          <div className="auth_analytics_stat-value">{analyticsData.uniqueUsers}</div>
        </div>
        <div className="auth_analytics_stat-card">
          <h3>Average Session</h3>
          <div className="auth_analytics_stat-value">
            {sessionStats.avg.toFixed(1)}m
            <div className="auth_analytics_stat-subtitle">
              Range: {sessionStats.min.toFixed(1)}m - {sessionStats.max.toFixed(1)}m
            </div>
          </div>
        </div>
        <div className="auth_analytics_stat-card">
          <h3>Most Active Device</h3>
          <div className="auth_analytics_stat-value">
            {Object.entries(analyticsData.deviceStats)
              .sort(([,a], [,b]) => b - a)[0]?.[0]?.split(' - ')[0] || 'N/A'}
          </div>
        </div>
      </div>
    );
  };

  // # Update chart configurations with real data
  const timeOfDayChartConfig = {
    labels: Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`),
    datasets: [{
      label: 'Logins by Hour',
      data: analyticsData.peakHours,
      borderColor: chartColors.primary,
      backgroundColor: `${chartColors.primary}33`,  // # 20% opacity
      fill: true,
      tension: 0.4,
      borderWidth: 2
    }],
    options: {
      scales: {
        y: {
          ticks: { color: chartColors.secondary },
          grid: { color: `${chartColors.secondary}22` }
        },
        x: {
          ticks: { color: chartColors.secondary },
          grid: { color: `${chartColors.secondary}22` }
        }
      }
    }
  };

  const weekdayChartConfig = {
    labels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    datasets: [{
      label: 'Logins by Day',
      data: [
        analyticsData.weekdayStats['Sunday'],
        analyticsData.weekdayStats['Monday'],
        analyticsData.weekdayStats['Tuesday'],
        analyticsData.weekdayStats['Wednesday'],
        analyticsData.weekdayStats['Thursday'],
        analyticsData.weekdayStats['Friday'],
        analyticsData.weekdayStats['Saturday']
      ],
      backgroundColor: chartColors.secondary,
      borderRadius: 6
    }]
  };

  const deviceChartConfig = {
    labels: Object.keys(analyticsData.deviceStats).map(device => device.split(' - ')[0]),
    datasets: [{
      data: Object.values(analyticsData.deviceStats),
      backgroundColor: chartColors.complementary,
      borderWidth: 0
    }]
  };

  // # Add session length distribution chart
  const sessionLengthChartConfig = {
    labels: ['0-5m', '5-15m', '15-30m', '30-60m', '60m+'],
    datasets: [{
      label: 'Session Length Distribution',
      data: analyticsData.sessionLengths.reduce((acc, length) => {
        if (length <= 5) acc[0]++;
        else if (length <= 15) acc[1]++;
        else if (length <= 30) acc[2]++;
        else if (length <= 60) acc[3]++;
        else acc[4]++;
        return acc;
      }, [0, 0, 0, 0, 0]),
      backgroundColor: chartColors.accent,
      borderRadius: 6
    }]
  };

  // # Add this function before renderLongTermTrends
  const getTimeRangeData = () => {
    const now = moment();
    let startDate;
    
    switch(timeRange) {
      case 'week':
        startDate = moment().subtract(7, 'days');
        break;
      case 'month':
        startDate = moment().subtract(1, 'month');
        break;
      case 'quarter':
        startDate = moment().subtract(3, 'months');
        break;
      case 'year':
        startDate = moment().subtract(1, 'year');
        break;
      case 'custom':
        startDate = moment(customRange.start);
        now.set(customRange.end);
        break;
      default:
        startDate = moment().subtract(1, 'month');
    }

    const dates = [];
    const currentDate = startDate.clone();

    while (currentDate.isSameOrBefore(now, 'day')) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate.add(1, 'day');
    }

    return dates;
  };

  // Update the longTermLoginChartConfig in renderLongTermTrends
  const dates = getTimeRangeData();
  const longTermLoginChartConfig = {
    labels: dates,
    datasets: [
      {
        label: 'Logins',
        data: dates.map(date => longTermData.dailyLogins[date] || 0),
        borderColor: chartColors.primary,
        backgroundColor: `${chartColors.primary}33`,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Logouts',
        data: dates.map(date => longTermData.dailyLogouts[date] || 0),
        borderColor: chartColors.accent,
        backgroundColor: `${chartColors.accent}33`,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const longTermSessionChartConfig = {
    labels: dates,
    datasets: [{
      label: 'Average Session Length (minutes)',
      data: dates.map(date => longTermData.averageSessions[date]?.toFixed(1) || 0),
      borderColor: chartColors.secondary,
      backgroundColor: `${chartColors.secondary}33`,
      fill: true,
      tension: 0.4
    }]
  };

  // # Update charts section with new options
  const renderCharts = () => (
    <div className="auth_analytics_charts-grid">
      <div className="auth_analytics_chart-container">
        <h3>Login Activity by Time of Day</h3>
        <Line 
          data={timeOfDayChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'top',
                labels: { color: chartColors.secondary }
              },
              title: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary }
              },
              x: {
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary }
              }
            }
          }}
        />
      </div>
      <div className="auth_analytics_chart-container">
        <h3>Login Activity by Day of Week</h3>
        <Bar 
          data={weekdayChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'top',
                labels: { color: chartColors.secondary }
              },
              title: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary }
              },
              x: {
                grid: { display: false },
                ticks: { color: chartColors.secondary }
              }
            }
          }}
        />
      </div>
      <div className="auth_analytics_chart-container">
        <h3>Device Distribution</h3>
        <Doughnut 
          data={deviceChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'right',
                labels: { color: chartColors.secondary }
              }
            }
          }}
        />
      </div>
      <div className="auth_analytics_chart-container">
        <h3>Session Length Distribution</h3>
        <Bar 
          data={sessionLengthChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'top',
                labels: { color: chartColors.secondary }
              },
              title: { display: false }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary },
                title: {
                  display: true,
                  text: 'Number of Sessions',
                  color: chartColors.secondary
                }
              },
              x: {
                grid: { display: false },
                ticks: { color: chartColors.secondary }
              }
            }
          }}
        />
      </div>
    </div>
  );

  // # Update the renderLongTermTrends function
  const renderLongTermTrends = () => (
    <>
      <div className="auth_analytics_chart-container">
        <h3>Daily Login/Logout Activity</h3>
        <Line 
          data={longTermLoginChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'top',
                labels: { color: chartColors.secondary }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary }
              },
              x: {
                grid: { color: `${chartColors.secondary}22` },
                ticks: { 
                  color: chartColors.secondary,
                  maxRotation: 45,
                  minRotation: 45,
                  callback: function(value, index) {
                    const total = dates.length;
                    const interval = Math.ceil(total / 10);
                    if (index % interval === 0) {
                      return moment(dates[index]).format('MMM DD');
                    }
                    return '';
                  }
                }
              }
            }
          }}
        />
      </div>
      <div className="auth_analytics_chart-container">
        <h3>Average Session Length by Day</h3>
        <Line 
          data={longTermSessionChartConfig}
          options={{
            responsive: true,
            plugins: {
              legend: { 
                position: 'top',
                labels: { color: chartColors.secondary }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: `${chartColors.secondary}22` },
                ticks: { color: chartColors.secondary }
              },
              x: {
                grid: { color: `${chartColors.secondary}22` },
                ticks: { 
                  color: chartColors.secondary,
                  maxRotation: 45,
                  minRotation: 45,
                  callback: function(value, index) {
                    const total = dates.length;
                    const interval = Math.ceil(total / 10);
                    if (index % interval === 0) {
                      return moment(dates[index]).format('MMM DD');
                    }
                    return '';
                  }
                }
              }
            }
          }}
        />
      </div>
    </>
  );

  // # Render filters section
  const renderFilters = () => (
    <div className="auth_analytics_user-select">
      <div className="auth_analytics_date-filters">
        <input
          type="date"
          className="auth_analytics_filter-input"
          value={dateRange.start}
          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
        />
        <input
          type="date"
          className="auth_analytics_filter-input"
          value={dateRange.end}
          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
        />
        {activeTab === 'user' && (
          <button
            className="auth_analytics_search-button"
            onClick={() => setSearchDialogOpen(true)}
          >
            <Search /> Select User
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="auth_analytics_admin-page">
      <SharedHeader />
      
      <main className="auth_analytics_admin-content">
        <div className="auth_analytics_admin-header">
          <button 
            className="auth_analytics_back-button"
            onClick={() => navigate('/admin/analytics')}
          >
            <i className="fas fa-arrow-left"></i> Back to Analytics
          </button>
          <h1>Authentication Analytics</h1>
        </div>

        <div className="auth_analytics_tabs">
          <button
            className={`auth_analytics_tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General Analytics
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'user' ? 'active' : ''}`}
            onClick={() => setActiveTab('user')}
          >
            User Analytics
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            Sessions
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'devices' ? 'active' : ''}`}
            onClick={() => setActiveTab('devices')}
          >
            Devices
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'browsers' ? 'active' : ''}`}
            onClick={() => setActiveTab('browsers')}
          >
            Browsers
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'longterm' ? 'active' : ''}`}
            onClick={() => setActiveTab('longterm')}
          >
            Long-term Trends
          </button>
          <button
            className={`auth_analytics_tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Authentication Dashboard
          </button>
        </div>

        {activeTab === 'sessions' ? (
          <AdvancedAuthlytics activeTab="sessions" />
        ) : activeTab === 'devices' ? (
          <AdvancedAuthlytics activeTab="devices" />
        ) : activeTab === 'browsers' ? (
          <AdvancedAuthlytics activeTab="browsers" />
        ) : activeTab === 'longterm' ? (
          <div>
            <div className="auth_analytics_time-range-selector">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="auth_analytics_filter-input"
              >
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 3 Months</option>
                <option value="year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
              
              {timeRange === 'custom' && (
                <div className="auth_analytics_custom-range">
                  <input
                    type="date"
                    value={customRange.start}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                    className="auth_analytics_filter-input"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={customRange.end}
                    onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                    className="auth_analytics_filter-input"
                  />
                </div>
              )}
            </div>
            <div className="auth_analytics_charts-grid">
              {renderLongTermTrends()}
            </div>
          </div>
        ) : activeTab === 'dashboard' ? (
          <AuthlyticsDashboard dateRange={dateRange} />
        ) : (
          <>
            {renderFilters()}
            {renderStatsDashboard()}
            {renderCharts()}
          </>
        )}
        {renderUserSearchDialog()}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default AuthAnalytics; 